import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatINR, fmtDate } from "@/lib/roles";
import { useAuth } from "@/lib/auth";
import { Search, Loader2, Megaphone, Plus } from "lucide-react";

const AD_TYPE_LABELS = {
  hoarding: "Hoarding", led_display: "LED Display", banner: "Banner",
  wall_wrap: "Wall Wrap", kiosk: "Kiosk", bus_shelter: "Bus Shelter",
};

export default function AdList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 15;

  const fetchList = () => {
    setLoading(true);
    const params = { page, limit };
    if (q) params.q = q;
    if (status && status !== "all") params.status = status;
    api.get("/advertisements", { params }).then((r) => {
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [page, status]);

  return (
    <div className="space-y-6" data-testid="ad-list-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Module</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-[#E8702A]" /> Advertisement Tax
          </h1>
          <p className="text-sm text-slate-500 mt-1">Hoarding registry, fee calculation, demand generation & renewal.</p>
        </div>
        <Link to="/advertisements/new"><Button className="bg-[#0F2942] hover:bg-[#1E3A5F] text-white" data-testid="btn-ad-new"><Plus className="w-4 h-4 mr-1" /> Register Ad</Button></Link>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <form onSubmit={(e) => { e.preventDefault(); setPage(1); fetchList(); }} className="relative flex-1 min-w-[240px] max-w-lg">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by code, advertiser or business" className="pl-9" data-testid="ad-search" />
          </form>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="ad-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="renewed">Renewed</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ad-table">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left py-3 px-4">Ad Code</th>
                    <th className="text-left py-3 px-4">Business / Advertiser</th>
                    <th className="text-left py-3 px-4 hidden md:table-cell">Type</th>
                    <th className="text-left py-3 px-4 hidden lg:table-cell">Size</th>
                    <th className="text-right py-3 px-4">Fee</th>
                    <th className="text-left py-3 px-4">Expires</th>
                    <th className="text-left py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((a) => (
                    <tr key={a.id} className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="py-3 px-4"><Link to={`/advertisements/${a.id}`} className="font-mono text-xs text-[#0F2942] dark:text-blue-300 underline" data-testid={`ad-link-${a.ad_code}`}>{a.ad_code}</Link></td>
                      <td className="py-3 px-4">
                        <div className="font-medium">{a.business_name}</div>
                        <div className="text-xs text-slate-500 truncate max-w-xs">{a.advertiser_name} · {a.location_description}</div>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell"><Badge variant="outline">{AD_TYPE_LABELS[a.ad_type] || a.ad_type}</Badge></td>
                      <td className="py-3 px-4 hidden lg:table-cell font-mono text-xs">{a.width_ft}×{a.height_ft} ft</td>
                      <td className="py-3 px-4 text-right font-mono">{formatINR(a.fee_amount)}</td>
                      <td className="py-3 px-4 text-xs">{fmtDate(a.end_date)}</td>
                      <td className="py-3 px-4">
                        <Badge className="capitalize" variant={a.status === "active" ? "default" : a.status === "expired" ? "destructive" : "outline"}>{a.status}</Badge>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-slate-500">No advertisements found.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <Button size="sm" variant="outline" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
